/**
 * CAI PAGE - Gestión de Constancias de Autorización de Impresión (SAR Honduras)
 * CRUD de CAIs con alertas de vencimiento y agotamiento de rangos.
 */

import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { differenceInDays, parseISO } from "date-fns";
import type { ColumnDef } from "@tanstack/react-table";
import {
  FileKey2,
  Plus,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Search,
  X,
} from "lucide-react";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import {
  useCAI,
  useActivateCAI,
  useDeactivateCAI,
  useDeleteCAI,
  type CAIListItem,
} from "@/hooks/use-facturacion";
import { getCAIs } from "@/hooks/use-facturacion";

import { CAIFormDialog } from "../components/cai-form-dialog";

// =============================================================================
// Alert helpers
// =============================================================================

function getCAIAlertStatus(item: CAIListItem): "expired" | "expiring" | "low" | "ok" {
  if (!item.is_active) return "ok";
  if (!item.esta_vigente) return "expired";

  const daysLeft = differenceInDays(parseISO(item.fecha_limite), new Date());
  if (daysLeft <= 30) return "expiring";

  // Check if < 10% of range remaining
  try {
    const fin = parseInt(item.rango_fin.split("-").pop() || "0", 10);
    const inicio = parseInt(item.rango_inicio.split("-").pop() || "0", 10);
    const total = fin - inicio;
    if (total > 0 && item.numeros_restantes / total < 0.1) return "low";
  } catch {
    // ignore parse errors
  }

  return "ok";
}

function AlertBadge({ status }: { status: "expired" | "expiring" | "low" | "ok" }) {
  const { t } = useTranslation();
  switch (status) {
    case "expired":
      return <Badge variant="destructive">{t("facturacion.cai.alerts.expired")}</Badge>;
    case "expiring":
      return (
        <Badge className="bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30">
          {t("facturacion.cai.alerts.expiring")}
        </Badge>
      );
    case "low":
      return (
        <Badge className="bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30">
          {t("facturacion.cai.alerts.lowRange")}
        </Badge>
      );
    default:
      return null;
  }
}

// =============================================================================
// Page
// =============================================================================

export default function CAIPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedCAIId, setSelectedCAIId] = useState<number | null>(null);
  const [selectedCAIForAction, setSelectedCAIForAction] = useState<CAIListItem | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  // Fetch full CAI data for editing
  const { data: selectedCAI } = useCAI(selectedCAIId || 0, { enabled: !!selectedCAIId });
  const isFormReady = selectedCAIId === null || !!selectedCAI;

  // Mutations
  const activateMutation = useActivateCAI({
    onSuccess: () => {
      toast.success(t("facturacion.cai.toast.activated"));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deactivateMutation = useDeactivateCAI({
    onSuccess: () => {
      toast.warning(t("facturacion.cai.toast.deactivated"));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeleteCAI({
    onSuccess: () => {
      toast.error(t("facturacion.cai.toast.deleted"));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Handlers
  const handleEdit = useCallback((item: CAIListItem) => {
    setSelectedCAIId(item.id);
    setIsFormOpen(true);
  }, []);

  const handleToggleStatus = useCallback((item: CAIListItem) => {
    setSelectedCAIForAction(item);
    setConfirmAction(item.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((item: CAIListItem) => {
    setSelectedCAIForAction(item);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleCreate = () => {
    setSelectedCAIId(null);
    setIsFormOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedCAIForAction || !confirmAction) return;
    if (confirmAction === "activate") activateMutation.mutate(selectedCAIForAction.id);
    else if (confirmAction === "deactivate") deactivateMutation.mutate(selectedCAIForAction.id);
    else if (confirmAction === "delete") deleteMutation.mutate(selectedCAIForAction.id);
    setIsConfirmOpen(false);
    setSelectedCAIForAction(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    toast.success(isNew ? t("facturacion.cai.toast.created") : t("facturacion.cai.toast.updated"));
    setSelectedCAIId(null);
    refetch();
  };

  // Columns
  const columns: ColumnDef<CAIListItem>[] = useMemo(
    () => [
      {
        accessorKey: "cai",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("facturacion.cai.columns.cai")} />
        ),
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.cai}</span>,
      },
      {
        id: "rango",
        header: () => t("facturacion.cai.columns.rango"),
        cell: ({ row }) => (
          <div className="text-xs font-mono">
            <div>{row.original.rango_inicio}</div>
            <div className="text-muted-foreground">{row.original.rango_fin}</div>
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: "fecha_limite",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t("facturacion.cai.columns.fechaLimite")} />
        ),
        cell: ({ row }) => <span className="text-sm">{row.original.fecha_limite}</span>,
      },
      {
        accessorKey: "sucursal_nombre",
        header: () => t("facturacion.cai.columns.sucursal"),
        cell: ({ row }) => <span className="text-sm">{row.original.sucursal_nombre}</span>,
        enableSorting: false,
      },
      {
        accessorKey: "punto_emision",
        header: () => t("facturacion.cai.columns.puntoEmision"),
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.punto_emision}</span>,
        enableSorting: false,
      },
      {
        accessorKey: "numeros_restantes",
        header: () => t("facturacion.cai.columns.restantes"),
        cell: ({ row }) => (
          <span className="text-sm font-medium">{row.original.numeros_restantes}</span>
        ),
        enableSorting: false,
      },
      {
        id: "estado",
        header: () => t("facturacion.cai.columns.estado"),
        cell: ({ row }) => {
          const item = row.original;
          const alertStatus = getCAIAlertStatus(item);
          return (
            <div className="flex flex-col gap-1">
              <Badge variant={item.is_active ? "default" : "secondary"}>
                {item.is_active ? t("common.active") : t("common.inactive")}
              </Badge>
              <AlertBadge status={alertStatus} />
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(item)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(item)}>
                  {item.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!item.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(item)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  // Server-side data table
  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getCAIs,
    queryKey: "cais",
    storageKey: "cais_page_size",
    defaultPageSize: 20,
    filters: [{ key: "is_active", defaultValue: "all", urlParam: "status" }],
  });

  const columnLabels = {
    cai: t("facturacion.cai.columns.cai"),
    fecha_limite: t("facturacion.cai.columns.fechaLimite"),
    numeros_restantes: t("facturacion.cai.columns.restantes"),
  };

  // Mobile card
  const renderMobileCard = useCallback(
    (item: CAIListItem) => {
      const alertStatus = getCAIAlertStatus(item);
      return (
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={item.is_active ? "default" : "secondary"} className="text-xs">
                {item.is_active ? t("common.active") : t("common.inactive")}
              </Badge>
              <AlertBadge status={alertStatus} />
            </div>
            <p className="font-mono text-xs mt-1 truncate">{item.cai}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {item.sucursal_nombre} &middot; {t("facturacion.cai.columns.puntoEmision")}:{" "}
              {item.punto_emision}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {t("facturacion.cai.columns.fechaLimite")}: {item.fecha_limite} &middot;{" "}
              {t("facturacion.cai.columns.restantes")}: {item.numeros_restantes}
            </p>
            <div className="flex gap-1 mt-3">
              <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                <Pencil className="h-3 w-3 mr-1" />
                {t("common.edit")}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleToggleStatus(item)}>
                    {item.is_active ? (
                      <>
                        <XCircle className="mr-2 h-4 w-4" />
                        {t("common.deactivate")}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        {t("common.activate")}
                      </>
                    )}
                  </DropdownMenuItem>
                  {!item.is_active && (
                    <DropdownMenuItem
                      onClick={() => handleDelete(item)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t("common.delete")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      );
    },
    [t, handleEdit, handleToggleStatus, handleDelete]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileKey2 className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t("facturacion.cai.title")}</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("facturacion.cai.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button size={isMobile ? "sm" : "default"} onClick={handleCreate}>
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("facturacion.cai.newCAI")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("facturacion.cai.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={filters.is_active}
                onValueChange={(value) => setFilter("is_active", value)}
              >
                <SelectTrigger className="w-full sm:w-[150px]">
                  <SelectValue placeholder={t("facturacion.cai.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("facturacion.cai.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="secondary" size="icon" onClick={clearFilters} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>
              {t("facturacion.cai.showingResults", { count: totalCount })}
            </CardDescription>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("facturacion.cai.noCAIs")}
            hideOnTablet={["punto_emision", "numeros_restantes"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <CAIFormDialog
        open={isFormOpen && isFormReady}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) setSelectedCAIId(null);
        }}
        cai={selectedCAI || null}
        onSuccess={handleFormSuccess}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("facturacion.cai.confirmActivate")
            : confirmAction === "delete"
              ? t("facturacion.cai.confirmDelete")
              : t("facturacion.cai.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("facturacion.cai.confirmActivateDesc")
            : confirmAction === "delete"
              ? t("facturacion.cai.confirmDeleteDesc")
              : t("facturacion.cai.confirmDeactivateDesc")
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
